"use client";

import { c as _c } from "react/compiler-runtime";
import { DesktopMenu } from "@/components/header/desktop-menu";
import { MobileProfileMenu } from "@/components/header/mobile-profile-menu";
import { Logo } from "@/components/rac/logo";
import { useSellerStoreData } from "@/hooks/global-multiple";
import { useAuth } from "@/hooks/use-auth";
import { trackEvent } from "@/utils/analytics/track-event";
import { trackNavItem } from "@/utils/analytics/track-navigation";
import { Plus } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { MenuItem } from "../homepage/types";
const getMenuItems = ({
  currentPath: _,
  isAuthenticated
}: {
  currentPath: string;
  isAuthenticated: boolean;
}) => {
  const baseItems: MenuItem[] = [{
    label: "Search",
    href: isAuthenticated ? "/preference" : "/bengaluru"
  }, {
    label: "Pricing And Services",
    href: "/pricing"
  }, {
    label: "Blog",
    href: "/blog"
  }
  // {
  // 	label: "Old Website",
  // 	href: `https://web.settlin.in${currentPath}`,
  // 	target: "_blank",
  // 	rel: "noopener noreferrer",
  // },
  ];
  return baseItems;
};
const getExtraMenuItems = ({
  relation,
  hasProperties
}: {
  relation?: string;
  hasProperties: boolean;
}) => {
  const extraMenuItems: MenuItem[] = [];
  if (relation === "seller" && hasProperties) {
    // Show Dashboard for sellers who have properties (hasProperties = true)
    // This means the seller has already posted properties and can manage them
    extraMenuItems.push({
      label: "Dashboard",
      href: "/dashboard/seller"
    });
  } else if (relation === "buyer") {
    // Show Dashboard for buyers regardless of properties
    // Buyers always get access to their dashboard to manage preferences and saved properties
    extraMenuItems.push({
      label: "Dashboard",
      href: "/dashboard/buyer"
    });
  } else {
    // Show "Post Property Free" in all other cases:
    // 1. User is not logged in (relation is undefined/null)
    // 2. User is logged in as seller but has no properties (hasProperties = false)
    // This encourages property posting for new/empty sellers and non-authenticated users
    extraMenuItems.push({
      icon: Plus,
      label: "Post Property Free",
      href: "/sell"
    });
  }
  return extraMenuItems;
};
export const Header = () => {
  const $ = _c(21);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isFirefox, setIsFirefox] = useState(false);
  const lastScrollY = useRef(0);
  const {
    isAuthenticated,
    user
  } = useAuth();
  const {
    hasRawUnitAndProperties: t0
  } = useSellerStoreData(_temp);
  const hasRawUnitAndProperties = t0 === undefined ? false : t0;
  const pathname = usePathname();
  const menuItems = getMenuItems({
    currentPath: pathname || "",
    isAuthenticated
  });
  const t1 = user?.relation;
  let t2;
  if ($[0] !== hasRawUnitAndProperties || $[1] !== t1) {
    t2 = getExtraMenuItems({
      relation: t1,
      hasProperties: hasRawUnitAndProperties
    });
    $[0] = hasRawUnitAndProperties;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const extraMenuItems = t2;
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const handleScroll = () => {
        const currentScrollY = window.scrollY;
        setIsScrolled(currentScrollY > 10);
        const scrollDifference = currentScrollY - lastScrollY.current;
        if (currentScrollY < 50) {
          setIsVisible(true);
        } else {
          if (scrollDifference > 10 && currentScrollY > 100) {
            setIsVisible(false);
          } else {
            if (scrollDifference < -10) {
              setIsVisible(true);
            }
          }
        }
        lastScrollY.current = currentScrollY;
      };
      setIsFirefox(navigator.userAgent.toLowerCase().includes("firefox"));
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => window.removeEventListener("scroll", handleScroll);
    };
    t4 = [];
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] !== pathname) {
    t5 = item => ({
      ...item,
      active: pathname === item.href
    });
    $[5] = pathname;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const itemsWithActive = menuItems.map(t5);
  let t6;
  if ($[7] !== isFirefox || $[8] !== isScrolled || $[9] !== isVisible) {
    t6 = () => {
      const visibilityClasses = isVisible ? "translate-y-0" : "-translate-y-full";
      if (isScrolled) {
        return isFirefox ? `${"fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ease-in-out"} ${visibilityClasses} bg-white border-b border-gray-200/70 shadow-md` : `${"fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ease-in-out"} ${visibilityClasses} bg-white/80 backdrop-blur-md border-b border-gray-200/70 shadow-md`;
      }
      return `${"fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ease-in-out"} ${visibilityClasses} bg-white border-b border-gray-200 shadow-subtle`;
    };
    $[7] = isFirefox;
    $[8] = isScrolled;
    $[9] = isVisible;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const getHeaderClasses = t6;
  const handleLogoClick = _temp2;
  const t7 = getHeaderClasses();
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex items-center h-full"><Link href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity no-underline hover:no-underline h-full" onClick={handleLogoClick}><div className="flex items-center h-full"><Logo className="h-10 w-auto text-primary-700" /><span className="text-xl md:text-2xl font-bold text-primary-700 tracking-tight font-heading no-underline ml-2">SETTLIN</span></div></Link></div>;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== extraMenuItems || $[13] !== itemsWithActive) {
    t9 = <div className="hidden lg:flex items-center h-full"><div className="flex items-center h-full"><DesktopMenu menuItems={itemsWithActive} extraMenuItems={extraMenuItems} /></div></div>;
    $[12] = extraMenuItems;
    $[13] = itemsWithActive;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="lg:hidden"><MobileProfileMenu /></div>;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t9) {
    t11 = <nav className="max-w-7xl mx-auto px-4"><div className="flex justify-between items-center h-16 md:h-20">{t8}{t9}{t10}</div></nav>;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== t7) {
    t12 = <header className={t7}>{t11}</header>;
    $[18] = t11;
    $[19] = t7;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  return t12;
};
function _temp(state) {
  return state;
}
function _temp2() {
  trackNavItem({
    action: "logo",
    category: "navigation"
  });
  trackEvent({
    google: {
      event: "select_content",
      options: {
        content_type: "navigation",
        item_id: "logo-home"
      }
    },
    matomo: {
      event: "logo-click",
      options: {
        category: "navigation"
      }
    }
  });
}